((root, factory) => {
  const config = typeof module === 'object' && module.exports
    ? require('./corkopia-product-config.js')
    : root.CorkopiaProductConfig;
  const model = factory(config);
  if (typeof module === 'object' && module.exports) module.exports = model;
  root.CorkopiaProductModel = model;
})(typeof globalThis !== 'undefined' ? globalThis : window, (defaultConfig) => {
  'use strict';

  /**
   * @typedef {'cork_flat'|'cork_round'|'half_round'|'branch'|'statement_piece'|'background_piece'} ProductFamily
   * @typedef {'available'|'available_unverified'|'sold'|'unavailable'|'invalid'|'unknown'} ProductAvailabilityState
   * @typedef {{value:number, unit:string, sourceValue:number, sourceUnit:string}} NormalizedMeasurement
   * @typedef {{id:string|null, sku:string|null, title:string, priceCents:number|null, compareAtPriceCents:number|null, available:boolean|null, inventoryQuantity:number|null, inventoryPolicy:string|null, image:ProductMedia|null}} ProductVariant
   * @typedef {{id:string|null, type:'image'|'video'|'external_video'|'model'|'unknown', url:string|null, previewUrl:string|null, alt:string, width:number|null, height:number|null, sources:Array<object>}} ProductMedia
   * @typedef {{weight:NormalizedMeasurement|null, maximumLength:NormalizedMeasurement|null, maximumWidth:NormalizedMeasurement|null, maximumHeight:NormalizedMeasurement|null, thickness:NormalizedMeasurement|null, outerDiameter:NormalizedMeasurement|null, innerDiameter:NormalizedMeasurement|null, openingWidth:NormalizedMeasurement|null, openingHeight:NormalizedMeasurement|null, openingDiameter:NormalizedMeasurement|null, curvature:number|string|null, surfaceArea:NormalizedMeasurement|null, cavityVolume:NormalizedMeasurement|null, branchDiameter:NormalizedMeasurement|null, branchSegments:Array<object>}} ProductMeasurements
   * @typedef {{animals:Array<string>, enclosures:Array<string>, humidity:Array<string>, buildRoles:Array<string>}} ProductCompatibility
   * @typedef {{estimatedVolume:NormalizedMeasurement|null, packingFactor:number|null, minimumBox:string|null, orientation:string|null, protectiveClearance:NormalizedMeasurement|null, shippingBehavior:string|null, volumeSource:'metafield'|'derived_bounding_box'|'missing', safeVolumeUtilization:number}} ProductPackingData
   * @typedef {{id:string, label:string, tone:string}} ProductBadge
   * @typedef {{code:string, path:string, message:string, severity:'error'|'warning'|'info'}} ProductValidationIssue
   * @typedef {{state:ProductAvailabilityState, productAvailable:boolean|null, variantAvailable:boolean|null, inventoryQuantity:number|null, inventoryStatus:string|null, sellable:boolean}} ProductAvailability
   * @typedef {{schemaVersion:string, source:string, identity:object, commerce:object, classification:object, measurements:ProductMeasurements, compatibility:ProductCompatibility, packing:ProductPackingData, media:object, merchandising:object, availability:ProductAvailability, variants:Array<ProductVariant>, validation:{valid:boolean, errors:Array<ProductValidationIssue>, warnings:Array<ProductValidationIssue>, issues:Array<ProductValidationIssue>}} Product
   */

  const round = (value, places = 4) => Number(Number(value).toFixed(places));
  const present = (value) => value !== null && value !== undefined && value !== '';
  const asString = (value) => present(value) ? String(value).trim() || null : null;
  const handleize = (value) => String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const pushIssue = (issues, code, path, message, severity = 'warning') => {
    if (issues.some((issue) => issue.code === code && issue.path === path)) return;
    issues.push({ code, path, message, severity });
  };

  const unwrap = (input) => {
    if (!present(input)) return null;
    if (Array.isArray(input)) return input;
    if (typeof input !== 'object') return input;
    if ('value' in input) {
      const value = input.value;
      if (value && typeof value === 'object' && 'value' in value && !Array.isArray(value)) return value.value;
      return value;
    }
    if ('handle' in input) return input.handle;
    return input;
  };

  const getMetafields = (raw) => raw?.metafields?.custom || raw?.metafields || raw?.product?.metafields?.custom || raw?.product?.metafields || {};

  const getField = (metafields, keys = []) => {
    for (const key of keys) {
      if (present(metafields?.[key])) return metafields[key];
    }
    return null;
  };

  const getFieldValue = (metafields, keys) => unwrap(getField(metafields, keys));

  const normalizeBoolean = (input) => {
    const value = unwrap(input);
    if (value === true || value === 1 || String(value).toLowerCase() === 'true') return true;
    if (value === false || value === 0 || String(value).toLowerCase() === 'false') return false;
    return null;
  };

  const normalizeList = (input) => {
    const value = unwrap(input);
    if (!present(value)) return [];
    if (Array.isArray(value)) return value.map((item) => asString(unwrap(item))).filter(Boolean);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.map((item) => asString(unwrap(item))).filter(Boolean);
        } catch {
          return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
        }
      }
      return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
    }
    return [];
  };

  const normalizeNumber = (input, path, issues, options = {}) => {
    const value = unwrap(input);
    if (!present(value)) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || (options.positive && parsed <= 0) || (options.nonNegative && parsed < 0)) {
      pushIssue(issues, 'invalid_numeric_metafield', path, `${path} must contain a ${options.positive ? 'positive ' : ''}finite number.`, options.severity || 'warning');
      return null;
    }
    return parsed;
  };

  const fieldUnit = (input, fallbackUnit = null) => {
    if (!input || typeof input !== 'object') return fallbackUnit;
    return asString(input.unit || input.value?.unit || fallbackUnit)?.toLowerCase() || null;
  };

  const normalizeMeasurement = (input, kind, path, issues, config, fallbackUnit = null) => {
    if (!present(input)) return null;
    const value = normalizeNumber(input, path, issues, { positive: true });
    if (value === null) return null;
    const units = config.measurementUnits[kind];
    const sourceUnit = fieldUnit(input, fallbackUnit || units.target);
    const factor = units.factors[sourceUnit];
    if (!Number.isFinite(factor)) {
      pushIssue(issues, 'invalid_measurement_unit', path, `${path} uses unsupported ${kind} unit "${sourceUnit || 'missing'}".`, 'warning');
      return null;
    }
    return {
      value: round(value * factor),
      unit: units.target,
      sourceValue: value,
      sourceUnit
    };
  };

  const normalizeFamily = (input, issues, config) => {
    const value = asString(unwrap(input));
    if (!value) return null;
    const handle = handleize(value);
    for (const [id, definition] of Object.entries(config.families)) {
      if (id === handle || definition.aliases.some((alias) => handleize(alias) === handle)) return id;
    }
    pushIssue(issues, 'invalid_family', 'classification.family', `Structured family "${value}" is not one of the six approved Corkopia families.`, 'error');
    return null;
  };

  const familyLabel = (family, config = defaultConfig) => config.families?.[family]?.label || null;

  const normalizeEnum = (input, definitions, path, issues) => {
    const value = asString(unwrap(input));
    if (!value) return null;
    const handle = handleize(value);
    if (Object.hasOwn(definitions || {}, handle)) return handle;
    pushIssue(issues, 'invalid_enum_value', path, `${path} contains unsupported value "${value}".`, 'warning');
    return null;
  };

  const normalizeCompatibilityList = (input, definition, path, issues, approved) => {
    const values = normalizeList(input).map(handleize).filter(Boolean);
    if (!approved && values.length) {
      pushIssue(issues, 'unapproved_compatibility_taxonomy', path, `${path} values are preserved but the compatibility taxonomy is not owner-approved.`, 'warning');
      return [...new Set(values)];
    }
    const allowed = definition?.labels || {};
    return [...new Set(values.filter((value) => {
      if (Object.hasOwn(allowed, value)) return true;
      pushIssue(issues, 'invalid_compatibility_value', path, `${path} contains unsupported value "${value}".`, 'warning');
      return false;
    }))];
  };

  const compatibilityLabels = (values, definition, fallback = true) => values.map((value) => definition?.labels?.[value] || (fallback ? value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : value));

  const normalizePriceCents = (input, path, issues) => {
    if (!present(input)) return null;
    if (typeof input === 'object' && present(input.amount)) {
      const amount = Number(input.amount);
      if (Number.isFinite(amount) && amount >= 0) return Math.round(amount * 100);
    }
    const value = Number(input);
    if (!Number.isFinite(value) || value < 0) {
      pushIssue(issues, 'invalid_price', path, `${path} must be a non-negative amount in minor currency units.`, 'error');
      return null;
    }
    return Math.round(value);
  };

  const normalizeMedia = (input) => {
    if (!input) return null;
    if (typeof input === 'string') {
      return {
        id: null,
        type: 'image',
        url: input,
        previewUrl: input,
        alt: '',
        width: null,
        height: null,
        sources: []
      };
    }
    const type = handleize(input.type || input.mediaType || input.media_type || 'unknown');
    const normalizedType = ['image', 'video', 'external_video', 'model'].includes(type) ? type : 'unknown';
    const image = input.image || input.previewImage || input.preview_image || {};
    return {
      id: asString(input.id),
      type: normalizedType,
      url: asString(input.url || image.url),
      previewUrl: asString(input.previewUrl || input.preview_url || image.url),
      alt: asString(input.alt || image.alt) || '',
      width: Number.isFinite(Number(input.width || image.width)) ? Number(input.width || image.width) : null,
      height: Number.isFinite(Number(input.height || image.height)) ? Number(input.height || image.height) : null,
      sources: Array.isArray(input.sources) ? input.sources : []
    };
  };

  const selectImageFallback = ({ selectedVariant, heroImage, gallery = [], fallback = null }) => {
    const candidates = [
      selectedVariant?.image,
      heroImage,
      ...gallery,
      fallback
    ].map(normalizeMedia).filter((item) => item?.url || item?.previewUrl);
    return candidates[0] || null;
  };

  const normalizeVariant = (variant, rawMedia, issues) => {
    const image = variant?.image || rawMedia.find((media) => String(media.id) === String(variant?.featuredMediaId || variant?.featured_media_id));
    return {
      id: asString(variant?.id),
      sku: asString(variant?.sku),
      title: asString(variant?.title) || 'Default',
      priceCents: normalizePriceCents(variant?.priceCents ?? variant?.price, 'commerce.variant.price', issues),
      compareAtPriceCents: normalizePriceCents(variant?.compareAtPriceCents ?? variant?.compareAtPrice ?? variant?.compare_at_price, 'commerce.variant.compareAtPrice', issues),
      available: typeof variant?.available === 'boolean' ? variant.available : null,
      inventoryQuantity: present(variant?.inventoryQuantity ?? variant?.inventory_quantity)
        ? normalizeNumber(variant.inventoryQuantity ?? variant.inventory_quantity, 'commerce.variant.inventoryQuantity', issues, { nonNegative: true })
        : null,
      inventoryPolicy: asString(variant?.inventoryPolicy || variant?.inventory_policy)?.toLowerCase() || null,
      image: normalizeMedia(image)
    };
  };

  const isExactPiece = ({ inventoryType, oneOfOne }, config = defaultConfig) => {
    const type = handleize(inventoryType);
    return oneOfOne === true || config.inventoryTypes.exact.includes(type);
  };

  const quantityLimit = (product) => product?.classification?.exactPiece || product?.commerce?.oneOfOne ? 1 : null;

  const determineSoldState = ({ exactPiece, productAvailable, variantAvailable, inventoryQuantity, inventoryStatus }, config = defaultConfig) => {
    if (!exactPiece) return false;
    const status = asString(inventoryStatus)?.toUpperCase();
    if (status && config.availability.unavailableInventoryStatuses.includes(status)) return status === 'SOLD' || status === 'ARCHIVED';
    if (inventoryQuantity === 0) return true;
    if (productAvailable === false || variantAvailable === false) return true;
    return false;
  };

  const classifySize = (measurements, explicitSize, config = defaultConfig, issues = []) => {
    const explicit = explicitSize ? handleize(unwrap(explicitSize)) : null;
    const allowed = config.sizeClasses.values || {};
    if (explicit && !Object.hasOwn(allowed, explicit)) {
      pushIssue(issues, 'invalid_size_class', 'classification.sizeClass', `Size class "${explicitSize}" is not configured.`, 'warning');
    }
    const explicitValid = explicit && Object.hasOwn(allowed, explicit) ? explicit : null;
    if (!config.sizeClasses.approved || !config.sizeClasses.thresholds.length) return explicitValid;

    const dimensions = [measurements.maximumLength, measurements.maximumWidth, measurements.maximumHeight]
      .map((measurement) => measurement?.value)
      .filter(Number.isFinite);
    const longest = dimensions.length ? Math.max(...dimensions) : null;
    if (longest === null) return explicitValid;

    const computed = config.sizeClasses.thresholds.find((band) => longest <= band.maxLongestDimensionIn)?.id
      || config.sizeClasses.thresholds.at(-1)?.overflowId
      || null;
    if (explicitValid && computed && explicitValid !== computed) {
      pushIssue(issues, 'conflicting_size_label', 'classification.sizeClass', `Explicit size "${explicitValid}" conflicts with configured size "${computed}".`, 'warning');
    }
    return computed || explicitValid;
  };

  const formatPrice = (cents, currency = 'USD', locale = 'en-US') => {
    if (!Number.isFinite(Number(cents))) return null;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents) / 100);
    } catch {
      return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
    }
  };

  const formatMeasurement = (measurement, options = {}) => {
    if (!measurement || !Number.isFinite(measurement.value)) return null;
    const digits = options.maximumFractionDigits ?? 2;
    const labels = { in: 'in', oz: 'oz', sq_in: 'sq in', cu_in: 'cu in' };
    return `${Number(measurement.value.toFixed(digits))} ${labels[measurement.unit] || measurement.unit}`;
  };

  const formatWeight = (measurement, options = {}) => {
    if (!measurement || measurement.unit !== 'oz') return formatMeasurement(measurement, options);
    if (options.preferPounds !== false && measurement.value >= 16) {
      const pounds = measurement.value / 16;
      return `${Number(pounds.toFixed(options.maximumFractionDigits ?? 2))} lb`;
    }
    return formatMeasurement(measurement, options);
  };

  const primaryMeasurements = (product) => {
    const m = product?.measurements || {};
    const dimensions = [m.maximumLength, m.maximumWidth, m.maximumHeight].filter(Boolean);
    switch (product?.classification?.family) {
      case 'cork_flat':
        return [m.maximumLength, m.maximumWidth, m.thickness || m.maximumHeight].filter(Boolean);
      case 'cork_round':
      case 'half_round':
        return [m.outerDiameter || m.maximumWidth, m.innerDiameter, m.maximumLength].filter(Boolean);
      case 'branch':
        return [m.branchDiameter || m.maximumWidth, m.maximumLength].filter(Boolean);
      default:
        return dimensions;
    }
  };

  const generateBadges = (product) => {
    const badges = [];
    if (product.commerce.oneOfOne) badges.push({ id: 'one_of_one', label: 'One of one', tone: 'default' });
    if (product.classification.newArrival) badges.push({ id: 'new_arrival', label: 'New arrival', tone: 'success' });
    if (product.classification.statementPiece) badges.push({ id: 'statement_piece', label: 'Statement piece', tone: 'default' });
    if (product.classification.oversized) badges.push({ id: 'oversized', label: 'Oversized', tone: 'warning' });
    if (product.availability.state === 'sold') badges.push({ id: 'sold', label: 'Sold', tone: 'sold' });
    normalizeList(product.merchandising.badgeLabels).forEach((label) => badges.push({ id: `custom_${handleize(label)}`, label, tone: 'default' }));
    return badges.filter((badge, index, list) => list.findIndex((candidate) => candidate.id === badge.id) === index);
  };

  const validateRequiredFields = (product, issues) => {
    const required = (condition, code, path, message) => {
      if (condition) pushIssue(issues, code, path, message, 'error');
    };
    required(!product.identity.shopifyProductId, 'missing_product_id', 'identity.shopifyProductId', 'Shopify product ID is required.');
    required(!product.identity.shopifyVariantId, 'missing_variant', 'identity.shopifyVariantId', 'At least one selected Shopify variant is required.');
    required(!product.identity.handle, 'missing_handle', 'identity.handle', 'Product handle is required.');
    required(!product.identity.sku, 'missing_sku', 'identity.sku', 'A stable variant SKU is required before launch.');
    required(product.commerce.priceCents === null || product.commerce.priceCents <= 0, 'missing_price', 'commerce.priceCents', 'An approved positive price is required before launch.');
    required(!product.media.heroImage, 'missing_hero_image', 'media.heroImage', 'A hero image is required before launch.');

    if (product.classification.exactPiece) {
      required(!product.classification.family, 'missing_family', 'classification.family', 'Exact pieces require one of the six approved families.');
      required(!product.measurements.weight, 'missing_weight', 'measurements.weight', 'Exact pieces require exact weight.');
      required(!product.measurements.maximumLength || !product.measurements.maximumWidth || !product.measurements.maximumHeight, 'missing_dimensions', 'measurements', 'Exact pieces require maximum length, width, and height.');
      required(!product.packing.minimumBox, 'missing_minimum_box', 'packing.minimumBox', 'Exact pieces require a minimum shipping box.');
      required(!product.packing.estimatedVolume, 'missing_packing_volume', 'packing.estimatedVolume', 'Exact pieces require explicit or safely derived packing volume.');
    }

    if (product.classification.exactPiece && product.variants.length > 1) {
      pushIssue(issues, 'multiple_variants_one_of_one', 'variants', 'A one-of-one exact piece should normally have one tracked variant.', 'warning');
    }
    if (product.classification.exactPiece && product.availability.inventoryQuantity === null) {
      pushIssue(issues, 'inventory_quantity_unavailable', 'availability.inventoryQuantity', 'Liquid did not expose inventory quantity; Shopify availability remains the runtime source.', 'warning');
    }
    if (product.commerce.oneOfOne && ['sold', 'unavailable'].includes(product.availability.state)) {
      pushIssue(issues, 'unavailable_one_of_one', 'availability.state', 'This one-of-one product is unavailable and must not accept quantity.', 'warning');
    }
  };

  const adaptShopifyProduct = (raw, options = {}) => {
    const config = options.config || defaultConfig;
    const issues = [];
    const source = raw?.product || raw || {};
    const metafields = getMetafields(raw);
    const keys = config.fieldKeys;
    const rawMedia = (raw?.media || source.media || []).map(normalizeMedia).filter(Boolean);
    const rawVariants = raw?.variants || source.variants || [];
    const variants = rawVariants.map((variant) => normalizeVariant(variant, rawMedia, issues));
    const selectedId = asString(raw?.selectedVariantId || source.selectedVariantId || source.selected_variant_id);
    const selectedVariant = variants.find((variant) => variant.id === selectedId) || variants.find((variant) => variant.available) || variants[0] || null;

    const inventoryType = asString(getFieldValue(metafields, keys.inventoryType));
    const oneOfOneValue = normalizeBoolean(getField(metafields, keys.oneOfOne));
    const exactPiece = isExactPiece({ inventoryType, oneOfOne: oneOfOneValue }, config);
    const oneOfOne = oneOfOneValue === null ? exactPiece : oneOfOneValue;
    const family = normalizeFamily(getField(metafields, keys.family), issues, config);

    const measurements = {
      weight: normalizeMeasurement(getField(metafields, keys.weight), 'weight', 'measurements.weight', issues, config, 'lb'),
      maximumLength: normalizeMeasurement(getField(metafields, keys.maximumLength), 'length', 'measurements.maximumLength', issues, config, 'in'),
      maximumWidth: normalizeMeasurement(getField(metafields, keys.maximumWidth), 'length', 'measurements.maximumWidth', issues, config, 'in'),
      maximumHeight: normalizeMeasurement(getField(metafields, keys.maximumHeight), 'length', 'measurements.maximumHeight', issues, config, 'in'),
      thickness: normalizeMeasurement(getField(metafields, keys.thickness), 'length', 'measurements.thickness', issues, config, 'in'),
      outerDiameter: normalizeMeasurement(getField(metafields, keys.outerDiameter), 'length', 'measurements.outerDiameter', issues, config, 'in'),
      innerDiameter: normalizeMeasurement(getField(metafields, keys.innerDiameter), 'length', 'measurements.innerDiameter', issues, config, 'in'),
      openingWidth: normalizeMeasurement(getField(metafields, keys.openingWidth), 'length', 'measurements.openingWidth', issues, config, 'in'),
      openingHeight: normalizeMeasurement(getField(metafields, keys.openingHeight), 'length', 'measurements.openingHeight', issues, config, 'in'),
      openingDiameter: normalizeMeasurement(getField(metafields, keys.openingDiameter), 'length', 'measurements.openingDiameter', issues, config, 'in'),
      curvature: getFieldValue(metafields, keys.curvature),
      surfaceArea: normalizeMeasurement(getField(metafields, keys.surfaceArea), 'area', 'measurements.surfaceArea', issues, config, 'sq_in'),
      cavityVolume: normalizeMeasurement(getField(metafields, keys.cavityVolume), 'volume', 'measurements.cavityVolume', issues, config, 'cu_in'),
      branchDiameter: normalizeMeasurement(getField(metafields, keys.branchDiameter), 'length', 'measurements.branchDiameter', issues, config, 'in'),
      branchSegments: Array.isArray(getFieldValue(metafields, keys.branchSegments)) ? getFieldValue(metafields, keys.branchSegments) : []
    };

    const explicitPackingVolume = normalizeMeasurement(getField(metafields, keys.packingVolume), 'volume', 'packing.estimatedVolume', issues, config, 'cu_in');
    const packingFactor = normalizeNumber(getField(metafields, keys.packingFactor), 'packing.packingFactor', issues, { positive: true })
      ?? config.packing.defaultPackingFactor;
    let estimatedVolume = explicitPackingVolume;
    let volumeSource = explicitPackingVolume ? 'metafield' : 'missing';
    if (!estimatedVolume && [measurements.maximumLength, measurements.maximumWidth, measurements.maximumHeight].every((value) => value?.value > 0) && packingFactor > 0) {
      estimatedVolume = {
        value: round(measurements.maximumLength.value * measurements.maximumWidth.value * measurements.maximumHeight.value * packingFactor),
        unit: 'cu_in',
        sourceValue: null,
        sourceUnit: 'derived'
      };
      volumeSource = 'derived_bounding_box';
      pushIssue(issues, 'derived_packing_volume', 'packing.estimatedVolume', 'Packing volume was derived from bounding dimensions and the configured packing factor.', 'warning');
    }

    const productAvailable = typeof source.available === 'boolean' ? source.available : null;
    const variantAvailable = selectedVariant?.available ?? null;
    const inventoryQuantity = selectedVariant?.inventoryQuantity ?? null;
    const inventoryStatus = asString(getFieldValue(metafields, keys.inventoryStatus))?.toUpperCase() || null;
    const sold = determineSoldState({ exactPiece, productAvailable, variantAvailable, inventoryQuantity, inventoryStatus }, config);
    let availabilityState = 'unknown';
    if (sold) availabilityState = 'sold';
    else if (!selectedVariant) availabilityState = 'invalid';
    else if (productAvailable === false || variantAvailable === false || (inventoryStatus && config.availability.unavailableInventoryStatuses.includes(inventoryStatus))) availabilityState = 'unavailable';
    else if (productAvailable === true && variantAvailable === true) availabilityState = inventoryQuantity === null && exactPiece ? 'available_unverified' : 'available';

    const heroCandidate = normalizeMedia(raw?.heroImage || source.heroImage || source.featuredImage || source.featured_image);
    const heroImage = selectImageFallback({ selectedVariant, heroImage: heroCandidate, gallery: rawMedia.filter((media) => media.type === 'image'), fallback: config.mediaFallback });
    const compatibility = {
      animals: normalizeCompatibilityList(getField(metafields, keys.animalCompatibility), config.compatibility.animal, 'compatibility.animals', issues, config.compatibility.approved),
      enclosures: normalizeCompatibilityList(getField(metafields, keys.enclosureCompatibility), config.compatibility.enclosure, 'compatibility.enclosures', issues, config.compatibility.approved),
      humidity: normalizeCompatibilityList(getField(metafields, keys.humiditySuitability), config.compatibility.humidity, 'compatibility.humidity', issues, true),
      buildRoles: normalizeCompatibilityList(getField(metafields, keys.buildRoles), config.compatibility.buildRole, 'compatibility.buildRoles', issues, true)
    };

    const product = {
      schemaVersion: config.schemaVersion,
      source: asString(raw?.source) || 'shopify',
      identity: {
        shopifyProductId: asString(source.id),
        shopifyVariantId: selectedVariant?.id || null,
        pieceId: asString(getFieldValue(metafields, keys.pieceId)),
        handle: asString(source.handle),
        sku: selectedVariant?.sku || null,
        title: asString(source.title) || '',
        description: asString(source.description || source.descriptionHtml || source.description_html) || '',
        vendor: asString(source.vendor),
        productType: asString(source.productType || source.product_type)
      },
      commerce: {
        priceCents: selectedVariant?.priceCents ?? null,
        compareAtPriceCents: selectedVariant?.compareAtPriceCents ?? null,
        currency: asString(raw?.currency || source.currency) || 'USD',
        oneOfOne,
        quantityLimit: null,
        selectedVariant
      },
      classification: {
        family,
        familyLabel: familyLabel(family, config),
        sizeClass: null,
        orientation: normalizeEnum(getField(metafields, keys.orientation), config.orientations, 'classification.orientation', issues),
        suggestedUses: normalizeList(getField(metafields, keys.suggestedUses)),
        newArrival: normalizeBoolean(getField(metafields, keys.newArrival)) === true,
        statementPiece: normalizeBoolean(getField(metafields, keys.statementPiece)) === true || family === 'statement_piece',
        oversized: normalizeBoolean(getField(metafields, keys.oversized)) === true,
        exactPiece,
        inventoryType,
        importBatch: asString(getFieldValue(metafields, keys.importBatch)),
        importBatchName: asString(getFieldValue(metafields, keys.importBatchName)),
        arrivalDate: asString(getFieldValue(metafields, keys.arrivalDate))
      },
      measurements,
      compatibility,
      packing: {
        estimatedVolume,
        packingFactor,
        minimumBox: asString(getFieldValue(metafields, keys.minimumBox)),
        orientation: normalizeEnum(getField(metafields, keys.orientation), config.orientations, 'packing.orientation', issues),
        protectiveClearance: normalizeMeasurement(getField(metafields, keys.protectiveClearance), 'length', 'packing.protectiveClearance', issues, config, 'in'),
        shippingBehavior: asString(getFieldValue(metafields, keys.shippingBehavior)),
        volumeSource,
        safeVolumeUtilization: config.packing.safeVolumeUtilization
      },
      media: {
        heroImage,
        gallery: rawMedia.filter((media) => media.type === 'image'),
        videos: rawMedia.filter((media) => ['video', 'external_video'].includes(media.type)),
        media360: normalizeList(getField(metafields, keys.media360)),
        measurementOverlay: normalizeMedia(getFieldValue(metafields, keys.measurementOverlay))
      },
      merchandising: {
        badgeLabels: normalizeList(getField(metafields, keys.badges)),
        tags: normalizeList(source.tags),
        collections: normalizeList(raw?.collections || source.collections).map(handleize),
        searchKeywords: normalizeList(getField(metafields, keys.searchKeywords)),
        recommendationLabels: normalizeList(getField(metafields, keys.recommendationLabels)),
        badges: []
      },
      availability: {
        state: availabilityState,
        productAvailable,
        variantAvailable,
        inventoryQuantity,
        inventoryStatus,
        sellable: false
      },
      variants,
      validation: { valid: false, errors: [], warnings: [], issues: [] }
    };

    product.classification.sizeClass = classifySize(measurements, getField(metafields, keys.sizeClass), config, issues);
    product.commerce.quantityLimit = quantityLimit(product);
    validateRequiredFields(product, issues);
    if (
      issues.some((issue) => issue.severity === 'error')
      && ['available', 'available_unverified'].includes(product.availability.state)
    ) {
      product.availability.state = 'invalid';
    }
    product.validation.issues = issues;
    product.validation.errors = issues.filter((issue) => issue.severity === 'error');
    product.validation.warnings = issues.filter((issue) => issue.severity !== 'error');
    product.validation.valid = product.validation.errors.length === 0;
    product.availability.sellable = ['available', 'available_unverified'].includes(product.availability.state) && product.validation.valid;
    product.merchandising.badges = generateBadges(product);
    return product;
  };

  return {
    adaptShopifyProduct,
    classifySize,
    compatibilityLabels,
    determineSoldState,
    familyLabel,
    formatMeasurement,
    formatPrice,
    formatWeight,
    generateBadges,
    handleize,
    isExactPiece,
    primaryMeasurements,
    quantityLimit,
    selectImageFallback
  };
});
